import { useHasAnyPermission } from "./useHasAnyPermission";
import { UserPermissions } from "../models/UserPermissions";
import { t } from "i18next";
import {
  faBuilding,
  faCakeCandles,
  faCalendarRange,
  faCashRegister,
  faFileChartPie,
  faGraduationCap,
  faHatChef,
  faHome,
  faLocationDot,
  faTableCells,
  faTrophyStar,
  faUserAstronaut,
  faUsers,
  IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import { useSlugs } from "./useSlugs";
import { faRocketLaunch } from "@fortawesome/pro-regular-svg-icons";
import { APP_LAUNCHER_COLOR } from "../../appLauncher/utils/appLauncherUtils";

export const useModules = () => {
  const { brandSlug, locationSlug } = useSlugs();

  const hasLocations = useHasAnyPermission([
    UserPermissions.LocationsAdmin,
    UserPermissions.LocationsManage,
    UserPermissions.DocumentsManage,
    UserPermissions.AttractionsAdmin,
    UserPermissions.AttractionsManage,
    UserPermissions.LocationsMigrateMemberships,
    UserPermissions.LocationsMigrateSchedules,
    UserPermissions.NotImplemented,
  ]);
  const hasWingman = useHasAnyPermission(UserPermissions.Wingman);

  const hasBooking = useHasAnyPermission([
    UserPermissions.BookingViewModule,
    UserPermissions.BookingViewEventScreen,
  ]);
  const hasScheduleManagement = useHasAnyPermission(
    UserPermissions.SchedulingEventTemplates,
  );
  const hasPointOfSale = useHasAnyPermission(UserPermissions.PosUser);
  const hasKitchenDisplaySystem = useHasAnyPermission(
    UserPermissions.NotImplemented
  );
  const hasLeagues = useHasAnyPermission(UserPermissions.NotImplemented);
  const hasReports = useHasAnyPermission([
    UserPermissions.DashboardView,
    UserPermissions.ReportsOperationsBooking,
    UserPermissions.ReportsAccounting,
    UserPermissions.ReportsDunning,
    UserPermissions.ReportsOperationsMigration,
    UserPermissions.ReportsTax,
    UserPermissions.ReportsReservationEnrollment,
    UserPermissions.ReportsRevenueReconciliation,
    UserPermissions.ReportsSales,
    UserPermissions.NotImplemented,
    UserPermissions.ReportsCorporateSales,
    UserPermissions.ReportsCorporateMigration,
    UserPermissions.ReportsAccountingTax,
    UserPermissions.ReportsAccountingReconciliation,
    UserPermissions.ReportsAccountingProfitLoss,
    UserPermissions.ReportsCorporateSystem,
    UserPermissions.DunningRetryPayment,
    UserPermissions.ReportsCalculatedSalesTax,
    UserPermissions.ReportsFranchiseeChargeback,
    UserPermissions.ReportsGiftCard,
    UserPermissions.ReportsCorporateDataFlow,
    UserPermissions.ReportsCorporateDataFlowLeadership,
  ]);
  const hasResourceCalendar = useHasAnyPermission(
    UserPermissions.NotImplemented
  );
  const hasCorporate = useHasAnyPermission([
    UserPermissions.PromotionsAdmin,
    UserPermissions.PromotionsManage,
    UserPermissions.QueuesAdmin,
    UserPermissions.PendingRefundsManage,
  ]);
  const hasUserManagement = useHasAnyPermission([
    UserPermissions.UsersManage,
    UserPermissions.UsersAdmin,
    UserPermissions.RolesAdmin,
    UserPermissions.UsersPin,
    UserPermissions.NotImplemented,
  ]);
  const hasKidHub = useHasAnyPermission(UserPermissions.PromoCardsManage);
  const hasAppLauncher = useHasAnyPermission([
    UserPermissions.AppLauncher,
    UserPermissions.AppLauncherAdmin,
  ]);

  const modules: {
    name: string;
    icon?: IconDefinition;
    image?: string;
    color: string;
    path: string;
  }[] = [
    {
      name: t`Home`,
      icon: faHome,
      color: "#0f0",
      path: "home",
    },
  ];

  if (hasLocations) {
    modules.push({
      name: t`Locations`,
      icon: faLocationDot,
      color: "#dd0097",
      path: "locations",
    });
  }

  if (hasBooking) {
    modules.push({
      name: t`Booking`,
      icon: faCakeCandles,
      color: "#58c0c2",
      path: "booking",
    });
  }

  if (hasScheduleManagement) {
    modules.push({
      name: t`Schedule Management`,
      icon: faGraduationCap,
      color: "#f56438",
      path: "schedule-management",
    });
  }

  if (hasPointOfSale) {
    modules.push({
      name: t`Point of Sale`,
      icon: faCashRegister,
      color: "#5f2a8b",
      path: "point-of-sale",
    });
  }

  if (hasKitchenDisplaySystem) {
    modules.push({
      name: t`Kitchen Display System`,
      icon: faHatChef,
      color: "#2C1B45",
      path: "kitchen-display-system",
    });
  }

  if (hasLeagues) {
    modules.push({
      name: t`Leagues`,
      icon: faTrophyStar,
      color: "#156833",
      path: "leagues",
    });
  }

  if (hasReports) {
    modules.push({
      name: t`Reports`,
      icon: faFileChartPie,
      color: "#f52839",
      path: "reports",
    });
  }

  if (hasResourceCalendar) {
    modules.push({
      name: t`Resource Calendar`,
      icon: faCalendarRange,
      color: "#5b5cab",
      path: "resource-calendar",
    });
  }

  if (hasCorporate) {
    modules.push({
      name: t`Corporate`,
      icon: faBuilding,
      color: "#E0198F",
      path: "corporate",
    });
  }

  if (hasUserManagement) {
    modules.push({
      name: t`User Management`,
      icon: faUsers,
      color: "#4d90cd",
      path: "user-management",
    });
  }

  if (hasKidHub) {
    modules.push({
      name: t`KidHub`,
      image: "/kidhub.png",
      color: "#822CBF",
      path: "kidhub",
    });
  }

  modules.push({
    name: t`Releases`,
    icon: faRocketLaunch,
    color: "#1e1e1e",
    path: "releases",
  });

  if (hasWingman) {
    modules.push({
      name: t`Wingman`,
      icon: faUserAstronaut,
      color: "#FBBC05",
      path: "wingman/chat",
    });
  }

  if (hasAppLauncher) {
    modules.push({
      name: t`App Launcher`,
      icon: faTableCells,
      color: APP_LAUNCHER_COLOR,
      path: "app-launcher",
    });
  }

  modules.forEach(
    (a) =>
      (a.path =
      brandSlug && locationSlug
        ? `/${brandSlug}/${locationSlug}/${a.path}`
        : brandSlug
          ? `/${brandSlug}/${a.path}`
          : a.path)
  );

  return modules;
};
